'use client';
import React, { useState } from 'react';
import { X, Navigation, Wifi, WifiOff, AlertTriangle, MapPin, Clock, Fuel, Gauge, Route, Phone, MessageSquare, ChevronDown } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';
import type { Asset } from './LiveTrackingView';

interface AssetDetailPanelProps {
  asset: Asset;
  onClose: () => void;
}

// Backend integration: POST /api/assets/:id/dispatch to send dispatch message
export default function AssetDetailPanel({ asset, onClose }: AssetDetailPanelProps) {
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  const handleSendMessage = () => {
    toast.success(`Message sent to ${asset.driver ?? asset.unitNumber}`);
  };

  const handleCall = () => {
    toast.info(`Calling ${asset.driver ?? asset.unitNumber}...`);
  };

  const routeProgress = asset.routeProgress ?? 0;

  return (
    <div className="w-80 flex-shrink-0 bg-zinc-900 border border-zinc-800 rounded-xl flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between px-4 py-3 border-b border-zinc-800">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-bold text-amber-400">{asset.unitNumber}</span>
            {asset.signal === 'lost'
              ? <WifiOff size={12} className="text-red-400" />
              : asset.signal === 'weak'
              ? <Wifi size={12} className="text-amber-400" />
              : <Wifi size={12} className="text-emerald-400" />
            }
          </div>
          <p className="text-xs text-zinc-400 mt-0.5">{asset.make} {asset.model}</p>
        </div>
        <button onClick={onClose} className="p-1 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-all">
          <X size={14} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {/* Status + quick actions */}
        <div className="px-4 py-3 border-b border-zinc-800 space-y-3">
          <div className="flex items-center justify-between">
            <div className="relative">
              <button
                onClick={() => setShowStatusMenu(!showStatusMenu)}
                className="flex items-center gap-1"
              >
                <StatusBadge status={asset.status as any} size="md" />
                <ChevronDown size={11} className="text-zinc-500" />
              </button>
              {showStatusMenu && (
                <div className="absolute top-full left-0 mt-1 bg-zinc-800 border border-zinc-700 rounded-lg py-1 z-50 min-w-[140px] shadow-xl">
                  {['available', 'dispatched', 'en-route', 'on-site', 'returning', 'maintenance', 'offline'].map((s) => (
                    <button
                      key={`status-opt-${s}`}
                      className="w-full text-left px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-700 transition-colors capitalize"
                      onClick={() => { setShowStatusMenu(false); toast.success(`Status updated to ${s}`); }}
                    >
                      {s.replace('-', ' ')}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              {asset.type !== 'equipment' && (
                <>
                  <button
                    onClick={handleCall}
                    className="p-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-zinc-400 hover:text-emerald-400 transition-all"
                  >
                    <Phone size={12} />
                  </button>
                  <button
                    onClick={handleSendMessage}
                    className="p-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-zinc-400 hover:text-sky-400 transition-all"
                  >
                    <MessageSquare size={12} />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Driver info */}
          {asset.driver && (
            <div className="flex items-center gap-2 bg-zinc-800 rounded-lg px-3 py-2">
              <div className="w-7 h-7 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-amber-400">
                  {asset.driver.split(' ').map((n) => n[0]).join('')}
                </span>
              </div>
              <div>
                <p className="text-xs font-semibold text-zinc-200">{asset.driver}</p>
                <p className="text-[10px] text-zinc-500">Assigned Driver</p>
              </div>
            </div>
          )}
        </div>

        {/* GPS / Position */}
        <div className="px-4 py-3 border-b border-zinc-800">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">GPS Position</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-zinc-800 rounded-lg px-3 py-2">
              <p className="text-[10px] text-zinc-500">Latitude</p>
              <p className="text-xs font-mono font-semibold text-zinc-200">{asset.lat.toFixed(4)}°</p>
            </div>
            <div className="bg-zinc-800 rounded-lg px-3 py-2">
              <p className="text-[10px] text-zinc-500">Longitude</p>
              <p className="text-xs font-mono font-semibold text-zinc-200">{asset.lng.toFixed(4)}°</p>
            </div>
            <div className="bg-zinc-800 rounded-lg px-3 py-2">
              <p className="text-[10px] text-zinc-500">Speed</p>
              <p className="text-xs font-mono font-semibold text-zinc-200 flex items-center gap-1">
                <Gauge size={10} className="text-zinc-400" />
                {asset.speed} mph
              </p>
            </div>
            <div className="bg-zinc-800 rounded-lg px-3 py-2">
              <p className="text-[10px] text-zinc-500">Heading</p>
              <p className="text-xs font-mono font-semibold text-zinc-200 flex items-center gap-1">
                <Navigation size={10} className="text-zinc-400" />
                {asset.heading}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-2 text-[10px] text-zinc-600 font-mono">
            <Clock size={9} />
            Last ping: {asset.lastPing}
          </div>
        </div>

        {/* Job info */}
        {asset.jobId && (
          <div className="px-4 py-3 border-b border-zinc-800">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Active Job</p>
            <div className="bg-zinc-800 rounded-lg px-3 py-2.5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs font-bold text-amber-400">{asset.jobId}</span>
                <StatusBadge status={asset.status as any} />
              </div>
              <div className="flex items-start gap-1.5 text-[11px] text-zinc-400">
                <MapPin size={10} className="mt-0.5 flex-shrink-0" />
                <span>{asset.jobSite}</span>
              </div>

              {/* Route progress */}
              {routeProgress > 0 && (
                <div>
                  <div className="flex items-center justify-between text-[10px] mb-1">
                    <span className="text-zinc-500 flex items-center gap-1"><Route size={9} /> Route Progress</span>
                    <span className="text-zinc-300 font-mono font-semibold">{routeProgress}%</span>
                  </div>
                  <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${routeProgress}%`,
                        backgroundColor: routeProgress >= 90 ? '#34D399' : routeProgress >= 50 ? '#F59E0B' : '#38BDF8',
                      }}
                    />
                  </div>
                </div>
              )}

              {asset.etaMinutes !== undefined && asset.etaMinutes > 0 && (
                <div className="flex items-center gap-1.5 text-[11px]">
                  <Clock size={10} className="text-zinc-500" />
                  <span className="text-zinc-500">ETA:</span>
                  <span className={`font-mono font-semibold ${asset.etaMinutes > 15 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {asset.etaMinutes} min
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Asset metrics */}
        <div className="px-4 py-3 border-b border-zinc-800">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Asset Metrics</p>
          <div className="space-y-2">
            {asset.fuelLevel !== undefined && (
              <div>
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="text-zinc-500 flex items-center gap-1"><Fuel size={10} /> Fuel Level</span>
                  <span className={`font-mono font-semibold ${asset.fuelLevel < 25 ? 'text-red-400' : asset.fuelLevel < 40 ? 'text-amber-400' : 'text-zinc-300'}`}>{asset.fuelLevel}%</span>
                </div>
                <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${asset.fuelLevel}%`,
                      backgroundColor: asset.fuelLevel < 25 ? '#F87171' : asset.fuelLevel < 40 ? '#F59E0B' : '#34D399',
                    }}
                  />
                </div>
              </div>
            )}
            {asset.odometer > 0 && (
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-zinc-500">Odometer</span>
                <span className="font-mono text-zinc-300">{asset.odometer.toLocaleString()} mi</span>
              </div>
            )}
          </div>
        </div>

        {/* Alerts */}
        {asset.alerts.length > 0 && (
          <div className="px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Alerts</p>
            <div className="space-y-1.5">
              {asset.alerts.map((alert, i) => (
                <div key={`alert-detail-${asset.id}-${i}`} className="flex items-start gap-2 bg-red-950/20 border border-red-500/20 rounded-lg px-3 py-2">
                  <AlertTriangle size={11} className="text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-red-300">{alert}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-4 py-3 border-t border-zinc-800 flex gap-2">
        <button className="flex-1 px-3 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-semibold rounded-lg transition-all duration-150 active:scale-95">
          View Full Profile
        </button>
        <button className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 text-xs font-semibold rounded-lg transition-all duration-150 active:scale-95">
          Dispatch
        </button>
      </div>
    </div>
  );
}